import React, {useEffect, useState} from 'react';
import {connect, DispatchProp} from "react-redux";
import {
    getAvailabilities,
    getCategories,
    getCustomFields,
    getItems,
    getMenus,
    getPromotions
} from "../../services/Menu/reducer";
import {useTranslation, withTranslation} from "react-i18next";
import {RootState} from "../../../reducers";
import {getStoreConfig} from "../../services/Store/reducer";
import {getSession} from "../../services/Session/reducer";
import {useSessionService} from "../../services/Session/sessionService";
import {ConfigAddressApi, DineInType, OrderType} from "../../services/Api/types";
import StoreUtils from "../../services/Store/StoreUtils";
import {createKeyDownHandler} from "../../utils/keyDownHanlder";
import CartPageDeliverySection from "./components/CartPage/CartOrderType/CartPageDeliverySection";
import CartPageDineInSection from "./components/CartPage/CartOrderType/CartPageDineInSection";
import CartPageRoomServiceSection from "./components/CartPage/CartOrderType/CartPageRoomServiceSection";
import CartPageRoomServiceOption from "./components/CartPage/CartOrderType/CartPageRoomServiceOption";
import CartPageDeliveryOption from "./components/CartPage/CartOrderType/CartPageDeliveryOption";
import CartPageDineInOption from "./components/CartPage/CartOrderType/CartPageDineInOption";
import CartPagePickUpOption from "./components/CartPage/CartOrderType/CartPagePickUpOption";
import AvailableHelper from "../../services/Menu/services/Availability/AvailableHelper";
import DateService from "../../services/Common/Date/DateService";
import {getParsedSessionOrderType} from "../../utils/order";
import {anyMenuHasPickDate} from "./utils";
import DisabledCartItemsModal from "./components/DisabledCartItemsModal/DisabledCartItemsModal";
import ItemHelper from "../../services/Menu/services/Item/ItemHelper";
import ItemViewHelper from "../../services/Menu/services/Item/ItemViewHelper";
import {getCart} from "../../services/Cart/reducer";
import {isTimeAllDay} from "../../services/Common/Date/DateHelper";
import HoursHelper from "../../services/Menu/services/Availability/HoursHelper";

interface OwnProps {
    errors: any[];
    onChange: (cart: any) => void;
    onChangeCustomField: (slug: any, value: any) => void;
    data: any;
    handleInputBlur: (field: string, value?: string) => void;
    setTableNumberModal: React.Dispatch<React.SetStateAction<boolean>>;
    tableNumberModal: boolean;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & DispatchProp;

const CartTypeComponent = (props: Props) => {
    const [roomNumberModal, setRoomNumberModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const orderType = props.session.order_type;
    const {t} = useTranslation();
    const sessionService = useSessionService();
    const dineInOptions: DineInType[] = StoreUtils.getDineInTypes(props.config);
    const [disabledCartItems, setDisabledCartItems] = useState<any[]>([]);
    const [lastOrderType, setLasOrderType] = useState<OrderType | "">(props.session.order_type as OrderType || "");
    const isStoreOpen = HoursHelper.isOpen(props.config?.hours || [], DateService.emptyOrNow(props.session.pickup_at));

    useEffect(() => {
        refreshLocation();
    }, [props.session.address.city, props.session.address.street, props.session.address.build_nr]);

    const handleChangeType = async (event: any, value: string, skipCheckingItems?: true) => {
        let type = value ?? event.target.value;
        setLasOrderType(type as OrderType);
        if (!skipCheckingItems) {
            const possibleDisabledItems = getPossibleDisabledItemsForType(type as OrderType);

            if (possibleDisabledItems.length > 0) {
                setDisabledCartItems(possibleDisabledItems);
                return;
            }
        }

        let pickupAt = props.session.pickup_at;
        setLoading(true);

        if (pickupAt && !anyMenuHasPickDate(props.menus, getParsedSessionOrderType(type))) {
            pickupAt = undefined;
        }

        switch (type) {
            case "DINE_IN":
                if (!StoreUtils.isOrderingPreview(props.config)) {
                    if (dineInOptions.length === 1) {
                        await sessionService.update({
                            ...props.session,
                            dine_in_type: dineInOptions[0],
                            order_type: type,
                            pickup_at: pickupAt
                        });
                        if (dineInOptions[0] === DineInType.TABLE) {
                            props.setTableNumberModal(true);
                        }
                    } else {
                        await sessionService.update({
                            ...props.session,
                            order_type: type,
                            pickup_at: pickupAt
                        });
                    }
                } else {
                    let sessionData = {
                        ...props.session,
                        order_type: type,
                        pickup_at: pickupAt
                    }
                    try {
                        await sessionService.update(sessionData);
                    } catch (error) {
                    }
                }
                break;
            case "ROOM_SERVICE":
                if (!StoreUtils.isOrderingPreview(props.config)) {
                    setRoomNumberModal(true)
                } else {
                    let sessionData = {
                        ...props.session,
                        order_type: type,
                        pickup_at: pickupAt
                    }
                    try {
                        await sessionService.update(sessionData);
                    } catch (error) {
                    }
                }
                break;
            default:
                let sessionData = {
                    ...props.session,
                    order_type: type,
                    pickup_at: pickupAt
                }
                try {
                    await sessionService.update(sessionData);
                } catch (error) {
                }
                break;
        }
        setLoading(false);
    }

    const refreshLocation = async () => {
        let addressData = props.session.address ? {
            city: props.session.address.city,
            street: props.session.address.street,
            build_nr: props.session.address.build_nr,
            flat_nr: props.session.address.flat_nr,
            zip_code: props.session.address.zip_code,
            country: props.session.address.country ? props.session.address.country : props.session?.address?.country
        } : {} as ConfigAddressApi;
        try {
            await sessionService.updateLocation(addressData);
        } catch (err: any) {
            console.log(err);
        }
    }

    const handleOnTypeLabelKeyDown = (orderType: string) => createKeyDownHandler<HTMLLabelElement>({
        keys: ["Enter", " "],
        preventDefault: true,
        action: (event) => handleChangeType(event, orderType)
    });

    const drawOpenHoursForOrderTypeOption = (orderType: OrderType) => {
        const parsedOrderType = getParsedSessionOrderType(orderType);
        const menuAvailabilities = AvailableHelper.getAvailabilities(props.availabilities, props.menus.map((menu: any) => menu[parsedOrderType.toLowerCase()] === true && menu.availability_id));
        const hours: {
            hour_from: string;
            hour_to: string;
        }[] = AvailableHelper.getHoursAvailable(menuAvailabilities, DateService.emptyOrNow(props.session.pickup_at)) || [];
        const isOpenForCurrentType = menuAvailabilities.some((availability: any) => AvailableHelper.isAvailabile(availability, DateService.emptyOrNow(props.session.pickup_at)));

        if (!isStoreOpen) {
            return <small className="text-danger">
                {t("enums.store_status.closed")}
            </small>
        }

        if (!isOpenForCurrentType) {
            return <small className="text-danger">
                {t("common.word.not_available")}
            </small>
        }

        const hoursTitle = hours.length === 1 && isTimeAllDay(hours[0].hour_from) && isTimeAllDay(hours[0].hour_to) ?
            t("modules.cart.field.whole_day.title") :
            hours.map(item => `${item.hour_from} - ${item.hour_to}`).join(", ");

        return (
            <small className="text-muted">
                {hoursTitle}
            </small>
        )
    }

    const getPossibleDisabledItemsForType = (type: OrderType) => {
        const parsedOrderType = getParsedSessionOrderType(orderType);
        const menuAvailabilities = AvailableHelper.getAvailabilities(props.availabilities, props.menus.map((menu: any) => menu[parsedOrderType.toLowerCase()] === true && menu.availability_id));
        const isOpenForCurrentType = menuAvailabilities.some((availability: any) => AvailableHelper.isAvailabile(availability, DateService.emptyOrNow(props.session.pickup_at)));
        const isOpen = isOpenForCurrentType && isStoreOpen;

        if (!isOpen) {
            return props.cart.items.map((cartItem: any) => ItemHelper.getItem(props.items, cartItem.item_id)).filter(Boolean);
        }

        const cartItems = props.cart.items.map((cartItem: any) => {

            const item = ItemHelper.getItem(props.items, cartItem.item_id);

            if (!item) return undefined;

            return {
                item,
                itemView: ItemViewHelper.parseItem(item, props.availabilities, props.promotions, props.session.pickup_at, undefined, props.cart, type)
            }
        }).filter(Boolean);

        return cartItems.filter(item => !ItemHelper.isItemAvailableForOrderType(item?.itemView, type, props.menus, props.categories))
            .map(item => item?.item).filter(Boolean);
    }

    const handleOnHideDisabledCartItemsModal = () => {
        setDisabledCartItems([]);
    }

    const handleOnSubmitDisabledCartItemsModal = async () => {
        if (lastOrderType) await handleChangeType(undefined, lastOrderType, true);
        handleOnHideDisabledCartItemsModal();
    }

    return (
        <React.Fragment>
            <div className="card-cart">
                <div className={"container-fluid"}>
                    <h5 className={"card-cart-header"}>
                        {t("modules.cart.order_type.field.title")}
                    </h5>
                    <div className={"card-cart-body"}>
                        <div>
                            <div className="btn-groups-columns">
                                <div className="btn-group1 btn-group-toggle btn-checks" data-toggle="buttons">
                                    <CartPageDeliveryOption handleChangeType={handleChangeType}
                                                            drawOpenHours={drawOpenHoursForOrderTypeOption}
                                                            loading={loading}
                                                            orderType={orderType}
                                                            handleOnTypeLabelKeyDown={handleOnTypeLabelKeyDown}/>
                                    <CartPagePickUpOption handleChangeType={handleChangeType}
                                                          loading={loading}
                                                          orderType={orderType}
                                                          drawOpenHours={drawOpenHoursForOrderTypeOption}
                                                          handleOnTypeLabelKeyDown={handleOnTypeLabelKeyDown}/>
                                    <CartPageDineInOption
                                        handleChangeType={handleChangeType}
                                        loading={loading}
                                        orderType={orderType}
                                        handleOnTypeLabelKeyDown={handleOnTypeLabelKeyDown}
                                        setTableNumberModal={props.setTableNumberModal}
                                        tableNumberModal={props.tableNumberModal}
                                        drawOpenHours={drawOpenHoursForOrderTypeOption}
                                    />
                                    <CartPageRoomServiceOption roomNumberModal={roomNumberModal}
                                                               setRoomNumberModal={setRoomNumberModal}
                                                               handleChangeType={handleChangeType}
                                                               loading={loading}
                                                               orderType={orderType}
                                                               handleOnTypeLabelKeyDown={handleOnTypeLabelKeyDown}
                                                               drawOpenHours={drawOpenHoursForOrderTypeOption}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {!StoreUtils.isOrderingPreview(props.config) && (
                <>
                    {orderType === "DELIVERY" &&
                        <CartPageDeliverySection errors={props.errors}
                                                 handleInputBlur={props.handleInputBlur}
                        />}
                    {orderType === "DINE_IN" &&
                        <CartPageDineInSection errors={props.errors} setTableNumberModal={props.setTableNumberModal}
                                               options={dineInOptions}/>}
                    {orderType === "ROOM_SERVICE" &&
                        <CartPageRoomServiceSection errors={props.errors} setRoomNumberModal={setRoomNumberModal}/>}
                </>
            )}
            {disabledCartItems.length > 0 && <DisabledCartItemsModal show={disabledCartItems.length > 0}
                                                                     items={disabledCartItems}
                                                                     type="ORDER_TYPE"
                                                                     orderType={lastOrderType}
                                                                     onSubmit={handleOnSubmitDisabledCartItemsModal}
                                                                     onHide={handleOnHideDisabledCartItemsModal}/>}
        </React.Fragment>
    )
}

const mapStateToProps = (state: RootState) => ({
    session: getSession(state.session),
    config: getStoreConfig(state.config),
    custom_fields: getCustomFields(state.menu),
    availabilities: getAvailabilities(state.menu),
    menus: getMenus(state.menu),
    cart: getCart(state.cart),
    promotions: getPromotions(state.menu),
    items: getItems(state.menu),
    categories: getCategories(state.menu),
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(CartTypeComponent));
